import { useEffect, useRef } from 'react';
import { t } from '../../../i18n/uiText';
import {
  afterReaderSelectionSettled,
  hasReaderTextSelection,
  isVerseTextPointerTarget,
  looksLikeTextSelectionGesture,
} from '../../../utils/readerTextSelection';
import { isChapterSwipeGestureActive } from './BfHooks';
import { BfIconSparkle, BfIconBookmark, BfIconShare, BfIconClose } from './BfIcons';
import { STUDY_TABS, studyTabLabel } from './studyTabs';
import { useHScroll } from './useHScroll';
import BfHScrollArrows from './BfHScrollArrows';

const TAP_SLOP_PX = 10;
const LONG_PRESS_MS = 360;

/**
 * BfVerse — премиальный стих без карточки/бордера.
 *
 * Поведение:
 * - Tap = onVerseTap (родитель показывает floating toolbar).
 * - Long-press = haptic + активирует стих (как tap, но не с текста).
 *
 * Совместим по пропсам с легаси VerseCard, чтобы драпать в ChapterView
 * без хирургических вмешательств.
 */
export default function BfVerse({
  verse,
  onVerseTap,
  isActive,
  isSaved = false,
  showInlineActions = false,
  onOpenStudy,
  onOpenParallels,
  onShare,
  onBookmark,
  isLoadingParallel = false,
  pendingStudyTab = null,
  onClose,
}) {
  const gestureRef = useRef(null);
  const longPressTimer = useRef(null);
  const gestureListenersRef = useRef(null);

  const tryHaptic = (kind = 'light') => {
    try {
      if (typeof window !== 'undefined' && window?.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred(kind);
      } else if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(kind === 'rigid' ? 12 : 6);
      }
    } catch {
      /* no-op */
    }
  };

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const clearGestureListeners = () => {
    const listeners = gestureListenersRef.current;
    if (!listeners) return;
    window.removeEventListener('pointermove', listeners.onMove);
    window.removeEventListener('pointerup', listeners.onEnd);
    window.removeEventListener('pointercancel', listeners.onEnd);
    gestureListenersRef.current = null;
  };

  const emitVerseTap = () => {
    afterReaderSelectionSettled(() => {
      if (hasReaderTextSelection()) return;
      tryHaptic('light');
      onVerseTap?.(verse);
    });
  };

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    if (hasReaderTextSelection()) return;

    const fromText = isVerseTextPointerTarget(e.target);
    clearGestureListeners();
    const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    gestureRef.current = {
      x: e.clientX,
      y: e.clientY,
      id: e.pointerId,
      cancelled: false,
      fromText,
      t0,
    };

    const onMove = (ev) => {
      const g = gestureRef.current;
      if (!g || ev.pointerId !== g.id) return;
      if (isChapterSwipeGestureActive()) {
        g.cancelled = true;
        return;
      }
      const dx = Math.abs(ev.clientX - g.x);
      const dy = Math.abs(ev.clientY - g.y);
      if (dx > TAP_SLOP_PX || dy > TAP_SLOP_PX) {
        g.cancelled = true;
      }
      if (hasReaderTextSelection()) {
        g.cancelled = true;
      }
    };
    const onEnd = () => {
      clearGestureListeners();
    };
    gestureListenersRef.current = { onMove, onEnd };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);

    if (!fromText) {
      clearLongPress();
      longPressTimer.current = setTimeout(() => {
        longPressTimer.current = null;
        const g = gestureRef.current;
        if (!g || g.cancelled || isChapterSwipeGestureActive() || hasReaderTextSelection()) {
          return;
        }
        tryHaptic('rigid');
        onVerseTap?.(verse);
      }, LONG_PRESS_MS);
    }
  };

  const onPointerUp = (e) => {
    const g = gestureRef.current;
    gestureRef.current = null;
    clearGestureListeners();
    clearLongPress();
    if (!g || e.pointerId !== g.id) return;

    const durationMs =
      (typeof performance !== 'undefined' ? performance.now() : Date.now()) - (g.t0 || 0);
    const dx = Math.abs(e.clientX - g.x);
    const dy = Math.abs(e.clientY - g.y);

    if (
      g.cancelled ||
      isChapterSwipeGestureActive() ||
      looksLikeTextSelectionGesture(g.x, g.y, e.clientX, e.clientY, durationMs)
    ) {
      return;
    }

    if (dx <= TAP_SLOP_PX && dy <= TAP_SLOP_PX) {
      emitVerseTap();
    }
  };

  const onPointerCancel = () => {
    gestureRef.current = null;
    clearGestureListeners();
    clearLongPress();
  };

  useEffect(
    () => () => {
      clearLongPress();
      clearGestureListeners();
    },
    []
  );

  return (
    <div
      className={
        showInlineActions ? 'bf-verse-stack bf-verse-stack--open' : 'bf-verse-stack'
      }
    >
      <div
        className="bf-verse"
        data-active={isActive ? 'true' : 'false'}
        data-saved={isSaved ? 'true' : 'false'}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onPointerLeave={onPointerCancel}
      >
        <sup
          className="bf-verse__num"
          role="button"
          tabIndex={0}
          aria-label={`${t('verse')} ${verse.verse}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onVerseTap?.(verse);
            }
          }}
        >
          {verse.verse}
        </sup>
        <span className="bf-verse__text">{verse.text}</span>
      </div>

      {showInlineActions && (
        <BfVerseToolbar
          onOpenStudy={onOpenStudy}
          onOpenParallels={onOpenParallels}
          onShare={onShare}
          onBookmark={onBookmark}
          isSaved={isSaved}
          isLoadingParallel={isLoadingParallel}
          pendingStudyTab={pendingStudyTab}
          onClose={onClose}
        />
      )}
    </div>
  );
}

/**
 * BfVerseToolbar — разбор стиха под активным стихом.
 * Слева листаются вкладки «Толкования» (нажатие открывает сразу нужную), справа — сохранить, поделиться, закрыть.
 */
function BfVerseToolbar({
  onOpenStudy,
  onOpenParallels,
  onShare,
  onBookmark,
  isSaved = false,
  isLoadingParallel,
  pendingStudyTab = null,
  onClose,
}) {
  const { ref: tabsRef, edges: fade, scrollStep } = useHScroll();

  const act = (fn) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    fn?.();
  };

  const primary = STUDY_TABS.filter((tab) => tab.primary);
  const minor = STUDY_TABS.filter((tab) => !tab.primary);
  const chip = (tab, index) => (
    <button
      key={tab.id}
      type="button"
      className={[
        'bf-verse-toolbar__chip',
        index === 0 ? 'bf-verse-toolbar__chip--lead' : '',
        tab.primary ? '' : 'bf-verse-toolbar__chip--minor',
      ]
        .filter(Boolean)
        .join(' ')}
      data-pending={pendingStudyTab === tab.id ? 'true' : undefined}
      aria-busy={pendingStudyTab === tab.id ? 'true' : undefined}
      onClick={act(() => onOpenStudy?.(tab.id))}
    >
      {index === 0 && <BfIconSparkle />}
      {studyTabLabel(tab)}
    </button>
  );

  return (
    <div className="bf-verse-toolbar" role="group" aria-label={t('verseActions')}>
      <div className="bf-hscroll">
        <div
          ref={tabsRef}
          className="bf-verse-toolbar__tabs"
          role="toolbar"
          aria-label={t('studyToolsAria')}
          data-fade-start={fade.start ? 'true' : 'false'}
          data-fade-end={fade.end ? 'true' : 'false'}
        >
          {primary.map(chip)}
          {onOpenParallels && (
            <button
              type="button"
              className="bf-verse-toolbar__chip"
              disabled={isLoadingParallel}
              onClick={act(onOpenParallels)}
            >
              {t('parallels')}
            </button>
          )}
          <span className="bf-verse-toolbar__sep" aria-hidden="true" />
          {minor.map((tab) => chip(tab, -1))}
        </div>
        <BfHScrollArrows edges={fade} onStep={scrollStep} />
      </div>
      <div className="bf-verse-toolbar__icons">
        {onBookmark && (
          <button
            type="button"
            className="bf-verse-toolbar__icon"
            data-on={isSaved ? 'true' : 'false'}
            aria-pressed={isSaved}
            aria-label={isSaved ? t('unsaveVerse') : t('saveVerse')}
            title={isSaved ? t('unsaveVerse') : t('saveVerse')}
            onClick={act(onBookmark)}
          >
            <BfIconBookmark />
          </button>
        )}
        {onShare && (
          <button
            type="button"
            className="bf-verse-toolbar__icon"
            aria-label={t('share')}
            title={t('share')}
            onClick={act(onShare)}
          >
            <BfIconShare />
          </button>
        )}
        {onClose && (
          <button
            type="button"
            className="bf-verse-toolbar__icon bf-verse-toolbar__icon--close"
            aria-label={t('close')}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={act(onClose)}
          >
            <BfIconClose />
          </button>
        )}
      </div>
    </div>
  );
}
